// src/components/admin/DeletionHistoryView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Trash2, History, Search, Filter, RefreshCw, Loader, Download, 
  FileText, FileSpreadsheet, Calendar, User, Database, AlertTriangle,
  CheckCircle, XCircle, Eye, ChevronDown, ChevronRight, Clock,
  RotateCcw, HardDrive, Info, Shield, Users, Table as TableIcon, X
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Stat Card Component ──────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-bold text-white mt-1" style={{ color }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
  </div>
);

// ─── Delete Type Badge ────────────────────────────────────────────────────────
const DeleteTypeBadge = ({ isHardDelete }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
    isHardDelete ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
  }`}>
    {isHardDelete ? <HardDrive size={10} /> : <Trash2 size={10} />}
    {isHardDelete ? 'HARD DELETE' : 'SOFT DELETE'}
  </span>
);

// ─── Restore Status Badge ─────────────────────────────────────────────────────
const RestoreStatusBadge = ({ isRestored }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
    isRestored ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
  }`}>
    {isRestored ? <CheckCircle size={10} /> : <XCircle size={10} />}
    {isRestored ? 'Restored' : 'Not Restored'}
  </span>
);

// ─── Restore Modal ────────────────────────────────────────────────────────────
const RestoreModal = ({ deletion, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold">Restore Deleted Record</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-300 text-sm">
            Are you sure you want to restore this record from <span className="text-white font-medium">{deletion?.table_name}</span>?
          </p>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Reason for restoration *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="Explain why this record is being restored..."
              required
            />
          </div>
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
          >
            <RotateCcw size={14} />
            Restore Record
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Detail Modal ────────────────────────────────────────────────────────────
const DetailModal = ({ deletion, onClose }) => {
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    if (deletion?.record_snapshot) {
      setSnapshot(deletion.record_snapshot);
    }
  }, [deletion]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-3xl max-h-[80vh] overflow-hidden border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
          <div>
            <h3 className="text-white font-semibold">Deletion Details</h3>
            <p className="text-gray-400 text-xs">Record from {deletion?.table_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-700/30 rounded-lg p-3">
              <p className="text-gray-500 text-xs">Deleted At</p>
              <p className="text-white text-sm">{new Date(deletion?.deleted_at).toLocaleString()}</p>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-3">
              <p className="text-gray-500 text-xs">Deleted By</p>
              <p className="text-white text-sm">{deletion?.deleted_by_name} ({deletion?.deleted_by_role})</p>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-3">
              <p className="text-gray-500 text-xs">Record ID</p>
              <p className="text-white text-xs font-mono">{deletion?.record_id}</p>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-3">
              <p className="text-gray-500 text-xs">Identifier</p>
              <p className="text-white text-sm">{deletion?.record_identifier || '—'}</p>
            </div>
          </div>
          
          <div className="bg-gray-700/30 rounded-lg p-3 mb-4">
            <p className="text-gray-500 text-xs mb-1">Reason for Deletion</p>
            <p className="text-gray-300 text-sm">{deletion?.reason}</p>
          </div>
          
          {snapshot && (
            <div>
              <p className="text-gray-400 text-xs font-medium mb-2">Record Snapshot (JSON)</p>
              <pre className="bg-gray-900 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto max-h-96">
                {JSON.stringify(snapshot, null, 2)}
              </pre>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const DeletionHistoryView = () => {
  const [deletions, setDeletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFilter, setTableFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('30d');
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [expandedId, setExpandedId] = useState(null);
  const [restoringDeletion, setRestoringDeletion] = useState(null);
  const [viewingDetail, setViewingDetail] = useState(null);
  
  // Available tables for filter
  const [availableTables, setAvailableTables] = useState([]);

  // Fetch company settings
  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/company/settings`);
      if (response.data) {
        setCompany(response.data);
        if (response.data.logo_url) {
          setLogoUrl(`${API_BASE}${response.data.logo_url}`);
        }
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  // Fetch deletion data
  const fetchDeletions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange !== 'all') {
        const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 30;
        params.append('days', days);
      }
      if (tableFilter !== 'all') params.append('table_name', tableFilter);
      if (typeFilter !== 'all') params.append('is_hard_delete', typeFilter === 'hard');
      
      const [deletionsRes, statsRes, tablesRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/deletion-audit/?${params}`),
        axios.get(`${API_BASE}/admin/deletion-audit/stats?days=${dateRange === 'all' ? 365 : (dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90)}`),
        axios.get(`${API_BASE}/admin/deletion-audit/tables`)
      ]);
      
      setDeletions(deletionsRes.data?.data || []);
      setStats(statsRes.data);
      setAvailableTables(tablesRes.data || []);
    } catch (error) {
      console.error('Error fetching deletion history:', error);
      setDeletions([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [dateRange, tableFilter, typeFilter]);

  useEffect(() => {
    fetchCompanySettings();
    fetchDeletions();
  }, [fetchDeletions, fetchCompanySettings]);

  // Filter deletions client-side
  const filteredDeletions = deletions.filter(del => {
    const matchesSearch = 
      del.record_identifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      del.deleted_by_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      del.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Restore record
  const handleRestore = async (deletion, reason) => {
    try {
      await axios.post(`${API_BASE}/admin/deletion-audit/${deletion.id}/restore`, { reason });
      await fetchDeletions();
      setRestoringDeletion(null);
    } catch (error) {
      console.error('Error restoring record:', error);
      alert(error.response?.data?.detail || 'Error restoring record');
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredDeletions.map(del => ({
      'Deleted At': new Date(del.deleted_at).toLocaleString(),
      'Deleted By': del.deleted_by_name,
      'Role': del.deleted_by_role,
      'Level': del.deleted_by_level,
      'Table': del.table_name,
      'Record ID': del.record_id,
      'Identifier': del.record_identifier,
      'Reason': del.reason,
      'Type': del.is_hard_delete ? 'Hard Delete' : 'Soft Delete',
      'Restored': del.is_restored ? 'Yes' : 'No',
      'Endpoint': del.endpoint
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Deletion History');
    XLSX.writeFile(wb, `deletion_history_${new Date().toISOString().slice(0,19)}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = async () => {
    const doc = new jsPDF();
    const companyName = company?.name || 'AeroTrick FBO';
    
    if (logoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = logoUrl;
        await new Promise((resolve) => {
          img.onload = () => {
            doc.addImage(img, 'PNG', 14, 10, 40, 15);
            resolve();
          };
          img.onerror = resolve;
        });
      } catch (e) {}
    }
    
    doc.setFontSize(18);
    doc.setTextColor(30, 64, 175);
    doc.text('Deletion Audit Report', 70, 20);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
    
    if (stats) {
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Deletions: ${stats.total_deletions} | Hard: ${stats.hard_deletions} | Soft: ${stats.soft_deletions}`, 14, 45);
    }
    
    const tableData = filteredDeletions.slice(0, 50).map(del => [
      new Date(del.deleted_at).toLocaleDateString(),
      del.deleted_by_name,
      del.table_name,
      del.record_identifier || '—',
      del.is_hard_delete ? 'Hard' : 'Soft',
      del.is_restored ? 'Yes' : 'No'
    ]);
    
    doc.autoTable({
      startY: 55,
      head: [['Date', 'Deleted By', 'Table', 'Identifier', 'Type', 'Restored']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 14, right: 14 }
    });
    
    const finalY = doc.lastAutoTable?.finalY || 250;
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Generated by AeroTrick for ${companyName} · © 2026 AeroTrick. All rights reserved.`,
      14,
      finalY + 20
    );
    
    doc.save(`deletion_history_${new Date().toISOString().slice(0,19)}.pdf`);
  };

  // Check if user can restore (level 90+)
  // This will be handled by backend, but UI shows button based on user permissions
  const canRestore = true; // Backend will enforce

  return (
    <div className="space-y-4 px-1">
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="h-10 w-auto object-contain"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/20 rounded-lg">
                <History size={18} className="text-blue-400" />
              </div>
              Deletion Audit Log
            </h2>
            <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
              <Clock size={11} />
              Last refresh: {lastRefresh.toLocaleTimeString()} — Tracks all soft and hard deletions
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportToExcel} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg text-green-400 text-xs">
            <FileSpreadsheet size={12} /> Excel
          </button>
          <button onClick={exportToPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-red-400 text-xs">
            <FileText size={12} /> PDF
          </button>
          <button onClick={fetchDeletions} className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard title="Total Deletions" value={stats.total_deletions} icon={Trash2} color="#ef4444" />
          <StatCard title="Soft Deletes" value={stats.soft_deletions} icon={Trash2} color="#f59e0b" />
          <StatCard title="Hard Deletes" value={stats.hard_deletions} icon={HardDrive} color="#dc2626" />
          <StatCard title="Restored" value={stats.soft_deletions - stats.current_deleted || 0} icon={RotateCcw} color="#10b981" />
          <StatCard title="Tables Affected" value={stats.by_table?.length || 0} icon={Database} color="#8b5cf6" />
        </div>
      )}
      
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by identifier, deleted by, or reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-9 pr-4 text-white text-sm"
          />
        </div>
        
        <select
          value={tableFilter}
          onChange={(e) => setTableFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Tables</option>
          {availableTables.map(t => (
            <option key={t.table_name} value={t.table_name}>
              {t.table_name} ({t.record_count})
            </option>
          ))}
        </select>
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Types</option>
          <option value="soft">Soft Deletes Only</option>
          <option value="hard">Hard Deletes Only</option>
        </select>
        
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>
      
      {/* Deletions Table */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Deleted At</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Deleted By</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Table</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Identifier</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Type</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Status</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-12 text-gray-500"><Loader className="animate-spin mx-auto" /></td></tr>
              ) : filteredDeletions.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-gray-500">No deletion records found</td></tr>
              ) : (
                filteredDeletions.map((del) => (
                  <React.Fragment key={del.id}>
                    <tr className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer"
                        onClick={() => setExpandedId(expandedId === del.id ? null : del.id)}>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {new Date(del.deleted_at).toLocaleString()}
                       </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User size={12} className="text-gray-500" />
                          <span className="text-gray-300 text-sm">{del.deleted_by_name}</span>
                          <span className="text-gray-500 text-xs">({del.deleted_by_role})</span>
                        </div>
                       </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-gray-300 text-sm">
                          <TableIcon size={12} /> {del.table_name}
                        </span>
                       </td>
                      <td className="px-4 py-3 text-gray-300 text-sm font-mono">
                        {del.record_identifier || del.record_id?.slice(0, 13) + '...'}
                       </td>
                      <td className="px-4 py-3">
                        <DeleteTypeBadge isHardDelete={del.is_hard_delete} />
                       </td>
                      <td className="px-4 py-3">
                        <RestoreStatusBadge isRestored={del.is_restored} />
                       </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setViewingDetail(del); }}
                            className="p-1 hover:bg-gray-600 rounded text-blue-400"
                            title="View details"
                          >
                            <Eye size={14} />
                          </button>
                          {!del.is_hard_delete && !del.is_restored && canRestore && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setRestoringDeletion(del); }}
                              className="p-1 hover:bg-gray-600 rounded text-green-400"
                              title="Restore record"
                            >
                              <RotateCcw size={14} />
                            </button>
                          )}
                          {expandedId === del.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>
                       </td>
                    </tr>
                    {expandedId === del.id && (
                      <tr className="bg-gray-800/40">
                        <td colSpan="7" className="px-4 py-4">
                          <div className="space-y-2">
                            <p className="text-gray-300 text-sm">
                              <span className="text-gray-500">Record ID:</span> 
                              <span className="font-mono ml-2">{del.record_id}</span>
                            </p>
                            <p className="text-gray-300 text-sm">
                              <span className="text-gray-500">Reason:</span> {del.reason}
                            </p>
                            {del.endpoint && (
                              <p className="text-gray-300 text-sm">
                                <span className="text-gray-500">API Endpoint:</span> {del.endpoint}
                              </p>
                            )}
                            {del.ip_address && (
                              <p className="text-gray-300 text-sm">
                                <span className="text-gray-500">IP Address:</span> {del.ip_address}
                              </p>
                            )}
                            {del.restored_at && (
                              <p className="text-gray-300 text-sm">
                                <span className="text-gray-500">Restored At:</span> {new Date(del.restored_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                         </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination info */}
      <div className="flex justify-between items-center text-xs text-gray-500">
        <span>Total records: {filteredDeletions.length}</span>
        {stats?.by_table && (
          <div className="flex gap-3">
            {stats.by_table.slice(0, 4).map(t => (
              <span key={t.table_name}>{t.table_name}: {t.count}</span>
            ))}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-gray-500 text-xs">
          Generated by AeroTrick for {company?.name || 'AeroTrick FBO'} · © 2026 AeroTrick. All rights reserved.
        </p>
      </div>
      
      {/* Modals */}
      {restoringDeletion && (
        <RestoreModal
          deletion={restoringDeletion}
          onConfirm={(reason) => handleRestore(restoringDeletion, reason)}
          onClose={() => setRestoringDeletion(null)}
        />
      )}
      
      {viewingDetail && (
        <DetailModal
          deletion={viewingDetail}
          onClose={() => setViewingDetail(null)}
        />
      )}
      
    </div>
  );
};

export default DeletionHistoryView;